import { Controller } from "@hotwired/stimulus"
import { cable } from "../channels/cable"

export default class extends Controller {
  static targets = ["studentTemplate", "teacherDotTemplate", "studentDotTemplate", "studentBadgeTemplate"]

  static values = {
    studyClassId: Number,
    courseId: Number,
    memberId: Number,
    memberName: String,
    role: String,       // "teacher" or "student"
    group: Boolean,
    courseUrl: String,
    lessonId: Number,   // initial lesson_id (0 = none)
    // i18n labels
    labelOnline: String,
    labelOffline: String,
    labelNoStudents: String,
    labelGoTo: String,
    labelTeacher: String,
    labelTeacherHere: String,
  }

  connect() {
    this.participants = {}
    this.channel = cable.subscribeTo("ClassroomPresenceChannel", {
      study_class_id: this.studyClassIdValue,
      course_id: this.courseIdValue,
    })

    this.unsubPresence = this.channel.on("presence", (event) => this.handlePresence(event))

    // Watch for lesson navigation via Turbo Frame
    this.boundOnFrameLoad = this.onFrameLoad.bind(this)
    document.addEventListener("turbo:frame-load", this.boundOnFrameLoad)

    // Wait for channel subscription, then join and sync presence
    this.channel.ensureSubscribed().then(() => {
      this.sendPresenceInfo(this.lessonIdValue > 0 ? this.lessonIdValue : null)
      this.syncPresence()
    })

    // Periodically sync presence state (handles missed events)
    this.syncInterval = setInterval(() => this.syncPresence(), 5000)
  }

  disconnect() {
    if (this.syncInterval) clearInterval(this.syncInterval)
    document.removeEventListener("turbo:frame-load", this.boundOnFrameLoad)
    if (this.unsubPresence) this.unsubPresence()
    if (this.channel) this.channel.disconnect()
  }

  syncPresence() {
    if (!this.channel || this.channel.state !== "connected") return

    this.channel.presence.reset()
    this.channel.presence.info().then((state) => {
      if (state) {
        this.participants = state
        this.render()
      }
    }).catch(() => {})
  }

  handlePresence(event) {
    if (event.type === "join") {
      this.participants[event.id] = event.info || {}
      this.render()
    } else if (event.type === "leave") {
      delete this.participants[event.id]
      this.render()
    } else if (event.type === "info") {
      // Full state refresh
      if (event.records) {
        this.participants = {}
        for (const record of event.records) {
          this.participants[record.id] = record.info || {}
        }
        this.render()
      }
    }
  }

  onFrameLoad(event) {
    if (event.target.id !== "lesson_viewer") return

    const url = new URL(event.target.src || event.detail?.url || window.location.href, window.location.origin)
    const lessonId = url.searchParams.get("lesson_id")

    if (lessonId) {
      this.sendPresenceInfo(parseInt(lessonId, 10))
    }

    // Turbo Frame replaced the DOM — re-render presence UI into fresh elements
    this.render()
  }

  sendPresenceInfo(lessonId) {
    const info = {
      name: this.memberNameValue,
      role: this.roleValue,
      group: this.groupValue,
    }
    if (lessonId) info.lesson_id = lessonId

    // Directly send $presence:join to set/update info (bypasses JS guard for re-join)
    this.channel.perform("$presence:join", {
      id: String(this.memberIdValue),
      info,
    })
  }

  // --- Rendering ---

  render() {
    this.renderPresenceContent()
    this.renderPresenceCount()
    this.renderTeacherBadge()
    this.renderNavIndicators()
  }

  get students() {
    return Object.entries(this.participants)
      .filter(([, info]) => info.role === "student")
      .map(([id, info]) => ({ id, ...info }))
  }

  get teacherOnline() {
    return Object.values(this.participants).some((info) => info.role === "teacher")
  }

  get activeLessons() {
    const lessons = {}
    for (const [, info] of Object.entries(this.participants)) {
      if (!info.lesson_id) continue
      const lid = info.lesson_id
      if (!lessons[lid]) lessons[lid] = { teacher_count: 0, student_count: 0 }
      if (info.role === "teacher") lessons[lid].teacher_count++
      else lessons[lid].student_count++
    }
    return lessons
  }

  renderPresenceContent() {
    const el = document.getElementById("classroom_presence_content")
    if (!el) return

    // Only teacher sees the student presence list
    if (this.roleValue !== "teacher") return

    const students = this.students

    if (this.groupValue) {
      this.renderGroupPresence(students)
    } else {
      this.renderIndividualPresence(students)
    }
  }

  renderGroupPresence(students) {
    const emptyEl = document.getElementById("classroom_presence_empty")
    const studentsEl = document.getElementById("classroom_presence_students")
    if (!emptyEl || !studentsEl) return

    if (students.length === 0) {
      emptyEl.classList.remove("hidden")
      studentsEl.classList.add("hidden")
      studentsEl.classList.remove("flex")
      studentsEl.replaceChildren()
    } else {
      emptyEl.classList.add("hidden")
      studentsEl.classList.remove("hidden")
      studentsEl.classList.add("flex")
      const fragment = document.createDocumentFragment()
      for (const student of students) {
        fragment.appendChild(this.buildStudentItem(student))
      }
      studentsEl.replaceChildren(fragment)
    }
  }

  renderIndividualPresence(students) {
    const onlineEl = document.getElementById("classroom_presence_online")
    const gotoEl = document.getElementById("classroom_presence_goto")
    const offlineEl = document.getElementById("classroom_presence_offline")
    if (!onlineEl || !gotoEl || !offlineEl) return

    if (students.length > 0) {
      const student = students[0]
      onlineEl.classList.remove("hidden")
      onlineEl.classList.add("inline-flex")
      offlineEl.classList.add("hidden")
      offlineEl.classList.remove("inline-flex")

      if (student.lesson_id) {
        gotoEl.href = `${this.courseUrlValue}?lesson_id=${student.lesson_id}`
        gotoEl.classList.remove("hidden")
      } else {
        gotoEl.classList.add("hidden")
      }
    } else {
      onlineEl.classList.add("hidden")
      onlineEl.classList.remove("inline-flex")
      gotoEl.classList.add("hidden")
      offlineEl.classList.remove("hidden")
      offlineEl.classList.add("inline-flex")
    }
  }

  buildStudentItem(student) {
    if (!this.hasStudentTemplateTarget) return document.createDocumentFragment()

    const clone = this.studentTemplateTarget.content.cloneNode(true)
    const link = clone.querySelector("[data-slot='link']")
    const name = clone.querySelector("[data-slot='name']")

    if (student.lesson_id) {
      link.href = `${this.courseUrlValue}?lesson_id=${student.lesson_id}`
      link.textContent = student.name
      link.classList.remove("hidden")
      name.classList.add("hidden")
    } else {
      name.textContent = student.name
    }

    return clone
  }

  renderPresenceCount() {
    const el = document.getElementById("classroom_presence_count")
    if (!el) return
    el.textContent = this.students.length
  }

  renderTeacherBadge() {
    const el = document.getElementById("classroom_teacher_badge")
    if (!el) return

    // Only students see the teacher badge
    if (this.roleValue !== "student") return

    if (this.teacherOnline) {
      el.classList.remove("hidden")
    } else {
      el.classList.add("hidden")
    }
  }

  renderNavIndicators() {
    const activeLessons = this.activeLessons

    document.querySelectorAll("[data-presence-indicator]").forEach((el) => {
      const lessonId = el.id.replace("lesson_nav_indicator_", "")
      const counts = activeLessons[lessonId]

      if (counts) {
        el.className = "ml-auto shrink-0 flex items-center gap-1"
        const fragment = document.createDocumentFragment()

        if (counts.teacher_count > 0 && this.hasTeacherDotTemplateTarget) {
          const dot = this.teacherDotTemplateTarget.content.cloneNode(true)
          const titleEl = dot.querySelector("[data-slot='title']")
          if (titleEl) titleEl.title = this.labelTeacherValue
          fragment.appendChild(dot)
        }

        if (counts.student_count === 1 && this.hasStudentDotTemplateTarget) {
          fragment.appendChild(this.studentDotTemplateTarget.content.cloneNode(true))
        } else if (counts.student_count > 1 && this.hasStudentBadgeTemplateTarget) {
          const badge = this.studentBadgeTemplateTarget.content.cloneNode(true)
          const countEl = badge.querySelector("[data-slot='count']")
          if (countEl) countEl.textContent = counts.student_count
          fragment.appendChild(badge)
        }

        el.replaceChildren(fragment)
      } else {
        el.className = ""
        el.replaceChildren()
      }
    })
  }
}
